import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiPlay,
  FiPause,
  FiSquare,
  FiTrash2,
  FiPlus,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiFolder,
  FiClock,
  FiCalendar,
} from "react-icons/fi";
import { timeEntryService, projectService } from "../api";
import style from "../style/TimeTracker.module.css";

const RUNNING_KEY = "gw-timetracker-running";
const NO_PROJECT = "__none__";

const pad = (n) => String(n).padStart(2, "0");

const fmtDuration = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${pad(m)}m`;
  if (m > 0) return `${m}m ${pad(s % 60)}s`;
  return `${s % 60}s`;
};

const fmtClock = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${pad(h)}:${pad(m)}:${pad(s % 60)}`;
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const startOfWeek = (d) => {
  const x = startOfDay(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};
const isSameDay = (a, b) => startOfDay(a).getTime() === startOfDay(b).getTime();

const dayKeyOf = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

// Grilla del mes (semanas de lunes a domingo) que contiene a `ref`.
const buildMonthGrid = (ref) => {
  const first = new Date(ref.getFullYear(), ref.getMonth(), 1);
  const start = startOfDay(first);
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7)); // retrocede al lunes
  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
};

const activeSecs = (r, nowMs) =>
  r ? r.accumulated + (r.segmentStart ? Math.floor((nowMs - new Date(r.segmentStart).getTime()) / 1000) : 0) : 0;

export default function TimeTracker() {
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [openProject, setOpenProject] = useState(undefined); // undefined = carpetas; obj|null(none)
  const [newProjectName, setNewProjectName] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [running, setRunning] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [saving, setSaving] = useState(false);
  const [menuFor, setMenuFor] = useState(null); // id de la entrada con menú abierto
  const [finishOpen, setFinishOpen] = useState(false);
  const [finishNotes, setFinishNotes] = useState("");
  const [expandedEntry, setExpandedEntry] = useState(null); // sesión abierta
  const [entryNotesDraft, setEntryNotesDraft] = useState("");
  const [projectNotesDraft, setProjectNotesDraft] = useState("");
  const [projectNotesSaved, setProjectNotesSaved] = useState(false);
  const [projectNotesOpen, setProjectNotesOpen] = useState(false);
  const [entryNotesOpen, setEntryNotesOpen] = useState(false);
  const [pauseMotivo, setPauseMotivo] = useState("");
  const [topView, setTopView] = useState("proyectos"); // proyectos | calendario
  const [calRef, setCalRef] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(null); // dayKey seleccionado en el calendario
  const [dayAddDate, setDayAddDate] = useState(null); // Date del día para "cargar trabajo"
  const [dayForm, setDayForm] = useState({ proyecto: NO_PROJECT, nuevo: "", descripcion: "", horas: "", minutos: "" });
  const [dayAddSaving, setDayAddSaving] = useState(false);
  const tickRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setError("");
    try {
      const [p, e] = await Promise.all([projectService.getAll(), timeEntryService.getAll()]);
      setProjects(Array.isArray(p.data) ? p.data : []);
      setEntries(Array.isArray(e.data) ? e.data : []);
    } catch {
      setError("No se pudieron cargar los datos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    try {
      const raw = localStorage.getItem(RUNNING_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.firstStart) {
          setRunning(parsed);
          setDescripcion(parsed.descripcion || "");
        }
      }
    } catch {
      /* nada */
    }
  }, [fetchAll]);

  useEffect(() => {
    if (!running || !running.segmentStart) {
      if (tickRef.current) clearInterval(tickRef.current);
      return undefined;
    }
    setNow(Date.now());
    tickRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tickRef.current);
  }, [running]);

  const persistRunning = (r) => {
    setRunning(r);
    try {
      if (r) localStorage.setItem(RUNNING_KEY, JSON.stringify(r));
      else localStorage.removeItem(RUNNING_KEY);
    } catch {
      /* nada */
    }
  };

  const currentProjectId = openProject === null ? null : openProject?._id;

  const handleStart = () => {
    persistRunning({
      proyecto: currentProjectId ?? null,
      descripcion: descripcion.trim(),
      firstStart: new Date().toISOString(),
      accumulated: 0,
      segmentStart: new Date().toISOString(),
      pausas: [],
      pauseStart: null,
    });
  };

  const handlePause = () => {
    if (!running?.segmentStart) return;
    const acc = running.accumulated + Math.floor((Date.now() - new Date(running.segmentStart).getTime()) / 1000);
    persistRunning({ ...running, accumulated: acc, segmentStart: null, pauseStart: new Date().toISOString() });
  };

  const handleResume = () => {
    if (!running || running.segmentStart) return;
    const pausas = [...(running.pausas || [])];
    if (running.pauseStart) {
      pausas.push({ inicio: running.pauseStart, fin: new Date().toISOString(), motivo: pauseMotivo.trim() });
    } else if (running.finTs) {
      // Quedó congelada por un Finalizar sin guardar (ej: refresh con el
      // modal abierto): ese hueco cuenta como pausa.
      pausas.push({ inicio: running.finTs, fin: new Date().toISOString(), motivo: "" });
    }
    setPauseMotivo("");
    persistRunning({ ...running, segmentStart: new Date().toISOString(), pauseStart: null, finTs: null, pausas });
  };

  // Finalizar congela el reloj en el momento del clic: la sesión termina acá.
  // "Guardar sesión" después sólo persiste (con la nota si querés).
  const handleOpenFinish = () => {
    if (!running) return;
    if (running.segmentStart) {
      const acc =
        running.accumulated +
        Math.floor((Date.now() - new Date(running.segmentStart).getTime()) / 1000);
      persistRunning({
        ...running,
        accumulated: acc,
        segmentStart: null,
        finTs: new Date().toISOString(),
      });
    } else {
      // Estaba en pausa: el trabajo terminó cuando arrancó esa pausa.
      persistRunning({ ...running, finTs: running.pauseStart || new Date().toISOString() });
    }
    setFinishOpen(true);
  };

  // Cancelar el finalizar: si venía corriendo retoma (el rato del modal queda
  // como pausa); si venía en pausa, sigue en pausa.
  const handleCancelFinish = () => {
    if (running?.finTs && !running.pauseStart) {
      const pausas = [
        ...(running.pausas || []),
        { inicio: running.finTs, fin: new Date().toISOString(), motivo: "" },
      ];
      persistRunning({
        ...running,
        segmentStart: new Date().toISOString(),
        finTs: null,
        pausas,
      });
    } else if (running?.finTs) {
      persistRunning({ ...running, finTs: null });
    }
    setFinishOpen(false);
  };

  const confirmFinish = async () => {
    if (!running || saving) return;
    setSaving(true);
    const total = activeSecs(running, Date.now());
    // La última pausa (si venía en pausa) no se registra: la sesión terminó
    // cuando esa pausa arrancó, no hubo trabajo después.
    const pausas = [...(running.pausas || [])];
    setPauseMotivo("");
    try {
      await timeEntryService.create({
        proyecto: running.proyecto || undefined,
        descripcion: running.descripcion || descripcion.trim(),
        notas: finishNotes,
        pausas,
        inicio: running.firstStart,
        fin: running.finTs || new Date().toISOString(),
        duracion: total,
      });
      persistRunning(null);
      setDescripcion("");
      setFinishNotes("");
      setFinishOpen(false);
      await fetchAll();
    } catch {
      setError("No se pudo guardar la sesión.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateProject = async (event) => {
    event.preventDefault();
    const nombre = newProjectName.trim();
    if (!nombre) return;
    try {
      const res = await projectService.create({ nombre });
      setProjects((prev) => [res.data, ...prev]);
      setNewProjectName("");
    } catch {
      setError("No se pudo crear el proyecto.");
    }
  };

  const handleDeleteEntry = async (id) => {
    setMenuFor(null);
    try {
      await timeEntryService.delete(id);
      setEntries((prev) => prev.filter((e) => e._id !== id));
    } catch {
      setError("No se pudo eliminar la sesión.");
    }
  };

  const handleDeleteProject = async (project, event, goBack = false) => {
    event?.stopPropagation?.();
    if (!window.confirm(`¿Borrar el proyecto "${project.nombre}"? Las sesiones quedan como "Sin proyecto".`)) {
      return;
    }
    try {
      await projectService.delete(project._id);
      setProjects((prev) => prev.filter((p) => p._id !== project._id));
      setEntries((prev) =>
        prev.map((e) => (e.proyecto === project._id ? { ...e, proyecto: null } : e))
      );
      if (goBack) setOpenProject(undefined);
    } catch {
      setError("No se pudo eliminar el proyecto.");
    }
  };

  const handleDeleteOrphans = async () => {
    const orphans = entries.filter((e) => !e.proyecto);
    if (!orphans.length) {
      window.alert("No hay sesiones sin proyecto para borrar.");
      return;
    }
    if (!window.confirm(`¿Borrar las ${orphans.length} sesiones sin proyecto? No se puede deshacer.`)) {
      return;
    }
    try {
      await Promise.all(orphans.map((e) => timeEntryService.delete(e._id)));
      setEntries((prev) => prev.filter((e) => e.proyecto));
      setOpenProject(undefined);
    } catch {
      setError("No se pudieron borrar las sesiones.");
    }
  };

  const toggleExpand = (entry) => {
    if (expandedEntry === entry._id) {
      setExpandedEntry(null);
    } else {
      setExpandedEntry(entry._id);
      setEntryNotesDraft(entry.notas || "");
      setEntryNotesOpen(false);
    }
  };

  const handleSaveEntryNotes = async (id) => {
    try {
      await timeEntryService.update(id, { notas: entryNotesDraft });
      setEntries((prev) => prev.map((e) => (e._id === id ? { ...e, notas: entryNotesDraft } : e)));
    } catch {
      setError("No se pudieron guardar las notas.");
    }
  };

  const handleSaveProjectNotes = async () => {
    if (!openProject?._id) return;
    try {
      await projectService.update(openProject._id, { notas: projectNotesDraft });
      setProjects((prev) =>
        prev.map((p) => (p._id === openProject._id ? { ...p, notas: projectNotesDraft } : p))
      );
      setOpenProject((prev) => (prev ? { ...prev, notas: projectNotesDraft } : prev));
      setProjectNotesSaved(true);
      setTimeout(() => setProjectNotesSaved(false), 1500);
    } catch {
      setError("No se pudieron guardar las notas del proyecto.");
    }
  };

  // Sincroniza el borrador de notas del proyecto al abrir una carpeta
  useEffect(() => {
    setProjectNotesDraft(openProject?.notas || "");
    setExpandedEntry(null);
  }, [openProject]);

  // Cerrar el menú de 3 puntitos al hacer clic en cualquier parte
  useEffect(() => {
    if (!menuFor) return undefined;
    const close = () => setMenuFor(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [menuFor]);

  // Totales por proyecto
  const totals = useMemo(() => {
    const map = new Map();
    const weekStart = startOfWeek(new Date());
    entries.forEach((e) => {
      const key = e.proyecto || NO_PROJECT;
      const cur = map.get(key) || { total: 0, week: 0, count: 0 };
      cur.total += Number(e.duracion) || 0;
      cur.count += 1;
      if (new Date(e.inicio) >= weekStart) cur.week += Number(e.duracion) || 0;
      map.set(key, cur);
    });
    return map;
  }, [entries]);

  // Nombre/color por proyecto (para el calendario)
  const projMeta = useMemo(() => {
    const map = new Map();
    projects.forEach((p) => map.set(p._id, { nombre: p.nombre, color: p.color || "#5dc72d" }));
    map.set(NO_PROJECT, { nombre: "Sin proyecto", color: "#8a94a6" });
    return map;
  }, [projects]);

  // Sesiones agrupadas por día (para el calendario)
  const byDay = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => {
      const key = dayKeyOf(e.inicio);
      const cur = map.get(key) || { total: 0, byProject: new Map(), items: [] };
      const dur = Number(e.duracion) || 0;
      cur.total += dur;
      const pk = e.proyecto || NO_PROJECT;
      cur.byProject.set(pk, (cur.byProject.get(pk) || 0) + dur);
      cur.items.push(e);
      map.set(key, cur);
    });
    return map;
  }, [entries]);

  const runningProject = running
    ? running.proyecto
      ? projects.find((p) => p._id === running.proyecto)
      : { nombre: "Sin proyecto" }
    : null;

  const elapsed = activeSecs(running, now);

  const goMonth = (delta) =>
    setCalRef((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));

  const openProjectById = (pid) => {
    if (pid === NO_PROJECT) return setOpenProject(null);
    const p = projects.find((x) => x._id === pid);
    if (p) setOpenProject(p);
  };

  const openDayAdd = (dateObj) => {
    setDayAddDate(dateObj);
    setDayForm({ proyecto: NO_PROJECT, nuevo: "", descripcion: "", horas: "", minutos: "" });
  };

  const submitDayEntry = async () => {
    if (!dayAddDate || dayAddSaving) return;
    const secs = (parseInt(dayForm.horas, 10) || 0) * 3600 + (parseInt(dayForm.minutos, 10) || 0) * 60;
    if (secs <= 0) {
      setError("Poné cuánto tiempo trabajaste (horas o minutos).");
      return;
    }
    setDayAddSaving(true);
    try {
      let proyectoId = dayForm.proyecto === NO_PROJECT ? undefined : dayForm.proyecto;
      if (dayForm.proyecto === "__new__") {
        const nombre = dayForm.nuevo.trim();
        if (!nombre) {
          setError("Poné el nombre del nuevo proyecto.");
          setDayAddSaving(false);
          return;
        }
        const res = await projectService.create({ nombre });
        setProjects((prev) => [res.data, ...prev]);
        proyectoId = res.data._id;
      }
      const inicio = new Date(dayAddDate.getFullYear(), dayAddDate.getMonth(), dayAddDate.getDate(), 9, 0, 0);
      const fin = new Date(inicio.getTime() + secs * 1000);
      await timeEntryService.create({
        proyecto: proyectoId,
        descripcion: dayForm.descripcion.trim(),
        inicio: inicio.toISOString(),
        fin: fin.toISOString(),
        duracion: secs,
      });
      setSelectedDay(dayKeyOf(dayAddDate));
      setDayAddDate(null);
      await fetchAll();
    } catch {
      setError("No se pudo cargar el trabajo.");
    } finally {
      setDayAddSaving(false);
    }
  };

  // ---------- Vista Calendario ----------
  const renderCalendar = () => {
    const cells = buildMonthGrid(calRef);
    const monthLabel = calRef.toLocaleDateString("es-AR", { month: "long", year: "numeric" });
    const todayKey = dayKeyOf(new Date());
    const dayData = selectedDay ? byDay.get(selectedDay) : null;

    return (
      <div className={style.calendarWrap}>
        <div className={style.calHead}>
          <button type="button" className={style.calNavBtn} onClick={() => goMonth(-1)} aria-label="Mes anterior">
            <FiChevronLeft />
          </button>
          <span className={style.calMonth}>{monthLabel}</span>
          <button type="button" className={style.calNavBtn} onClick={() => goMonth(1)} aria-label="Mes siguiente">
            <FiChevronRight />
          </button>
        </div>

        <div className={style.calWeekdays}>
          {WEEKDAYS.map((w) => (
            <span key={w}>{w}</span>
          ))}
        </div>

        <div className={style.calGrid}>
          {cells.map((d) => {
            const key = dayKeyOf(d);
            const data = byDay.get(key);
            const inMonth = d.getMonth() === calRef.getMonth();
            return (
              <div
                key={key}
                role="button"
                tabIndex={0}
                className={`${style.calCell} ${!inMonth ? style.calCellMuted : ""} ${
                  key === todayKey ? style.calCellToday : ""
                } ${data ? style.calCellHas : ""} ${selectedDay === key ? style.calCellSel : ""}`}
                onClick={() => setSelectedDay(key)}
                onKeyDown={(ev) => ev.key === "Enter" && setSelectedDay(key)}
              >
                <div className={style.calCellTop}>
                  <span className={style.calDayNum}>{d.getDate()}</span>
                  <button
                    type="button"
                    className={style.calAddBtn}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      openDayAdd(d);
                    }}
                    aria-label="Cargar trabajo este día"
                    title="Cargar trabajo"
                  >
                    <FiPlus />
                  </button>
                </div>
                {data ? <span className={style.calDayTotal}>{fmtDuration(data.total)}</span> : null}
              </div>
            );
          })}
        </div>

        {selectedDay ? (
          <div className={style.calDetail}>
            <div className={style.calDetailHead}>
              <strong>
                {new Date(`${selectedDay}T00:00:00`).toLocaleDateString("es-AR", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                })}
              </strong>
              <span className={style.calDetailTotal}>{fmtDuration(dayData?.total || 0)}</span>
            </div>
            <div className={style.calDetailList}>
              {dayData ? (
                [...dayData.byProject.entries()]
                  .sort((a, b) => b[1] - a[1])
                  .map(([pk, secs]) => {
                    const meta = projMeta.get(pk) || { nombre: "Proyecto", color: "#5dc72d" };
                    return (
                      <button
                        key={pk}
                        type="button"
                        className={style.calProjRow}
                        onClick={() => openProjectById(pk)}
                      >
                        <span className={style.calProjName}>
                          <FiFolder style={{ color: meta.color }} /> {meta.nombre}
                        </span>
                        <span className={style.calProjRight}>
                          <span className={style.calProjDur}>{fmtDuration(secs)}</span>
                          <FiPlay className={style.calProjPlay} title="Ir al temporizador" />
                        </span>
                      </button>
                    );
                  })
              ) : (
                <p className={style.calHint}>Todavía no cargaste trabajo este día.</p>
              )}
            </div>
            <button
              type="button"
              className={style.calAddWork}
              onClick={() => openDayAdd(new Date(`${selectedDay}T00:00:00`))}
            >
              <FiPlus /> Cargar trabajo en este día
            </button>
          </div>
        ) : (
          <p className={style.calHint}>
            Tocá un día para ver o cargar el trabajo. Usá el <strong>+</strong> del día para registrar.
          </p>
        )}

        {dayAddDate ? (
          <div className={style.overlay} onClick={() => setDayAddDate(null)} role="presentation">
            <div className={style.modal} onClick={(ev) => ev.stopPropagation()}>
              <h3>
                Cargar trabajo ·{" "}
                {dayAddDate.toLocaleDateString("es-AR", { day: "numeric", month: "long" })}
              </h3>
              <div className={style.field}>
                <span>Proyecto</span>
                <select
                  className={style.daySelect}
                  value={dayForm.proyecto}
                  onChange={(e) => setDayForm((f) => ({ ...f, proyecto: e.target.value }))}
                >
                  <option value={NO_PROJECT}>Sin proyecto</option>
                  {projects.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.nombre}
                    </option>
                  ))}
                  <option value="__new__">＋ Nuevo proyecto…</option>
                </select>
              </div>
              {dayForm.proyecto === "__new__" ? (
                <div className={style.field}>
                  <span>Nombre del nuevo proyecto</span>
                  <input
                    value={dayForm.nuevo}
                    onChange={(e) => setDayForm((f) => ({ ...f, nuevo: e.target.value }))}
                    placeholder="Ej: Cliente X"
                    maxLength={80}
                  />
                </div>
              ) : null}
              <div className={style.field}>
                <span>¿Qué hiciste? (opcional)</span>
                <input
                  value={dayForm.descripcion}
                  onChange={(e) => setDayForm((f) => ({ ...f, descripcion: e.target.value }))}
                  placeholder="Ej: Diseño de la landing"
                  maxLength={160}
                />
              </div>
              <div className={style.dayTimeRow}>
                <div className={style.field}>
                  <span>Horas</span>
                  <input
                    type="number"
                    min="0"
                    value={dayForm.horas}
                    onChange={(e) => setDayForm((f) => ({ ...f, horas: e.target.value }))}
                    placeholder="0"
                  />
                </div>
                <div className={style.field}>
                  <span>Minutos</span>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={dayForm.minutos}
                    onChange={(e) => setDayForm((f) => ({ ...f, minutos: e.target.value }))}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className={style.modalActions}>
                <button type="button" className={style.ghostBtn} onClick={() => setDayAddDate(null)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className={style.saveBtn}
                  onClick={submitDayEntry}
                  disabled={dayAddSaving}
                >
                  {dayAddSaving ? "Guardando…" : "Guardar"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  // ---------- Vista carpetas ----------
  if (openProject === undefined) {
    const noneTotals = totals.get(NO_PROJECT);
    return (
      <div className={style.foldersWrap}>
        <div className={style.topToggle}>
          <button
            type="button"
            className={`${style.topToggleBtn} ${topView === "proyectos" ? style.topToggleActive : ""}`}
            onClick={() => setTopView("proyectos")}
          >
            <FiFolder /> Proyectos
          </button>
          <button
            type="button"
            className={`${style.topToggleBtn} ${topView === "calendario" ? style.topToggleActive : ""}`}
            onClick={() => setTopView("calendario")}
          >
            <FiCalendar /> Calendario
          </button>
        </div>

        {topView === "calendario" ? renderCalendar() : null}
        {topView === "calendario" ? null : (
        <>
        <form className={style.createRow} onSubmit={handleCreateProject}>
          <input
            className={style.createInput}
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            placeholder="Crear trabajo o proyecto…"
            maxLength={80}
          />
          <button type="submit" className={style.createBtn} aria-label="Crear proyecto">
            <FiPlus />
          </button>
        </form>

        {running ? (
          <button
            type="button"
            className={style.runningBanner}
            onClick={() => setOpenProject(running.proyecto ? runningProject : null)}
          >
            <span className={style.runningDot} />
            Sesión {running.segmentStart ? "en curso" : "en pausa"} en{" "}
            <strong>{runningProject?.nombre || "Sin proyecto"}</strong> · {fmtClock(elapsed)}
          </button>
        ) : null}

        {error ? <p className={style.error}>{error}</p> : null}

        {loading ? (
          <p className={style.empty}>Cargando…</p>
        ) : projects.length === 0 && !noneTotals ? (
          <p className={style.empty}>
            Todavía no tenés proyectos. Creá uno arriba para empezar a registrar tus horas.
          </p>
        ) : (
          <div className={style.folderGrid}>
            {projects.map((p) => {
              const t = totals.get(p._id) || { total: 0, week: 0, count: 0 };
              return (
                <div
                  key={p._id}
                  className={style.folderCard}
                  role="button"
                  tabIndex={0}
                  onClick={() => setOpenProject(p)}
                  onKeyDown={(e) => e.key === "Enter" && setOpenProject(p)}
                >
                  <div className={style.folderTop}>
                    <span className={style.folderIcon} style={{ color: p.color || "#5dc72d" }}>
                      <FiFolder />
                    </span>
                    <button
                      type="button"
                      className={style.folderDelete}
                      onClick={(e) => handleDeleteProject(p, e)}
                      aria-label="Eliminar proyecto"
                      title="Eliminar proyecto"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                  <span className={style.folderName}>{p.nombre}</span>
                  <span className={style.folderTotal}>{fmtDuration(t.total)}</span>
                  <span className={style.folderMeta}>
                    {t.count} {t.count === 1 ? "sesión" : "sesiones"} · esta semana {fmtDuration(t.week)}
                  </span>
                </div>
              );
            })}

            {noneTotals ? (
              <div
                className={style.folderCard}
                role="button"
                tabIndex={0}
                onClick={() => setOpenProject(null)}
                onKeyDown={(e) => e.key === "Enter" && setOpenProject(null)}
              >
                <div className={style.folderTop}>
                  <span className={style.folderIcon} style={{ color: "#8a94a6" }}>
                    <FiFolder />
                  </span>
                </div>
                <span className={style.folderName}>Sin proyecto</span>
                <span className={style.folderTotal}>{fmtDuration(noneTotals.total)}</span>
                <span className={style.folderMeta}>
                  {noneTotals.count} {noneTotals.count === 1 ? "sesión" : "sesiones"}
                </span>
              </div>
            ) : null}
          </div>
        )}
        </>
        )}
      </div>
    );
  }

  // ---------- Vista detalle de proyecto ----------
  const projName = openProject === null ? "Sin proyecto" : openProject.nombre;
  const projEntries = entries
    .filter((e) => (e.proyecto || null) === currentProjectId)
    .sort((a, b) => new Date(b.inicio) - new Date(a.inicio));
  const projTotals = totals.get(currentProjectId || NO_PROJECT) || { total: 0, week: 0 };
  const isRunningHere = running && (running.proyecto || null) === currentProjectId;
  const runningElsewhere = running && !isRunningHere;

  return (
    <div className={style.detailWrap}>
      <div className={style.detailHead}>
        <button type="button" className={style.backBtn} onClick={() => setOpenProject(undefined)}>
          <FiChevronLeft />
          <span className={style.backLabel}>Proyectos</span>
        </button>
        <div className={style.detailTitleWrap}>
          <h2 className={style.detailTitle}>{projName}</h2>
          {openProject?.createdAt ? (
            <span className={style.detailDate}>
              Proyecto del{" "}
              {new Date(openProject.createdAt).toLocaleDateString("es-AR", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          className={style.detailDelete}
          onClick={() =>
            openProject
              ? handleDeleteProject(openProject, { stopPropagation: () => {} }, true)
              : handleDeleteOrphans()
          }
          aria-label={openProject ? "Eliminar proyecto" : "Borrar sesiones sin proyecto"}
          title={openProject ? "Eliminar proyecto" : "Borrar sesiones sin proyecto"}
        >
          <FiTrash2 />
        </button>
      </div>

      <div className={style.detailGrid}>
        <div className={style.detailLeft}>
      {/* Cronómetro */}
      <section className={style.timerCard}>
        <input
          className={style.descInput}
          value={isRunningHere ? running.descripcion : descripcion}
          onChange={(e) =>
            isRunningHere
              ? persistRunning({ ...running, descripcion: e.target.value })
              : setDescripcion(e.target.value)
          }
          placeholder="¿En qué estás trabajando?"
          maxLength={160}
        />

        <div className={style.clock}>{fmtClock(isRunningHere ? elapsed : 0)}</div>

        {runningElsewhere ? (
          <p className={style.warnNote}>
            Ya tenés una sesión en curso en <strong>{runningProject?.nombre}</strong>. Finalizala
            antes de arrancar otra.
          </p>
        ) : !running ? (
          <button type="button" className={`${style.bigBtn} ${style.startBtn}`} onClick={handleStart}>
            <FiPlay /> Iniciar
          </button>
        ) : (
          <div className={style.timerBtns}>
            {running.segmentStart ? (
              <button type="button" className={`${style.midBtn} ${style.pauseBtn}`} onClick={handlePause}>
                <FiPause /> Pausa
              </button>
            ) : (
              <button type="button" className={`${style.midBtn} ${style.resumeBtn}`} onClick={handleResume}>
                <FiPlay /> Reanudar
              </button>
            )}
            <button
              type="button"
              className={`${style.midBtn} ${style.stopBtn}`}
              onClick={handleOpenFinish}
              disabled={saving}
            >
              <FiSquare /> Finalizar
            </button>
          </div>
        )}

        {isRunningHere && !running.segmentStart ? (
          <input
            className={style.pauseInput}
            value={pauseMotivo}
            onChange={(e) => setPauseMotivo(e.target.value)}
            placeholder="Motivo de la pausa (ej: comida) — opcional"
            maxLength={120}
          />
        ) : null}

        <div className={style.totalsRow}>
          <div className={style.totalCard}>
            <span>Total del proyecto</span>
            <strong>{fmtDuration(projTotals.total + (isRunningHere ? elapsed : 0))}</strong>
          </div>
          <div className={style.totalCard}>
            <span>Esta semana</span>
            <strong>{fmtDuration(projTotals.week + (isRunningHere ? elapsed : 0))}</strong>
          </div>
        </div>
      </section>

      {/* Notas del proyecto (colapsable) */}
      {openProject ? (
        <section className={style.listCard}>
          <button
            type="button"
            className={style.collapseHead}
            onClick={() => setProjectNotesOpen((v) => !v)}
          >
            <span className={style.collapseTitle}>
              <FiClock /> Notas del proyecto
            </span>
            <FiChevronDown
              className={`${style.collapseChevron} ${projectNotesOpen ? style.collapseChevronOpen : ""}`}
            />
          </button>
          {projectNotesOpen ? (
            <div className={style.notesBody}>
              <textarea
                className={style.notesArea}
                value={projectNotesDraft}
                onChange={(e) => setProjectNotesDraft(e.target.value)}
                placeholder="Datos del cliente, pendientes, links… (notas del proyecto)"
                rows={5}
              />
              <button
                type="button"
                className={style.saveNotesBtn}
                onClick={handleSaveProjectNotes}
              >
                {projectNotesSaved ? "✓ Guardado" : "Guardar notas"}
              </button>
            </div>
          ) : null}
        </section>
      ) : null}
        </div>

      {/* Sesiones del proyecto (columna derecha) */}
      <section className={`${style.listCard} ${style.sessionsCol}`}>
        <div className={style.listHead}>
          <h2>
            <FiClock /> Sesiones
          </h2>
        </div>

        {error ? <p className={style.error}>{error}</p> : null}

        {projEntries.length === 0 ? (
          <p className={style.empty}>Todavía no registraste sesiones en este proyecto.</p>
        ) : (
          <ul className={style.entryList}>
            {projEntries.map((e) => {
              const start = new Date(e.inicio);
              const end = new Date(e.fin);
              const isOpen = expandedEntry === e._id;
              const pausas = Array.isArray(e.pausas) ? e.pausas : [];
              return (
                <li key={e._id} className={style.entryLi}>
                  <div className={`${style.entryItem} ${isOpen ? style.entryItemOpen : ""}`}>
                    <button
                      type="button"
                      className={style.entryMain}
                      onClick={() => toggleExpand(e)}
                    >
                      <strong>{e.descripcion || "Sin descripción"}</strong>
                      <span className={style.entryTime}>
                        {isSameDay(start, new Date())
                          ? "Hoy"
                          : start.toLocaleDateString("es-AR", { day: "numeric", month: "short" })}{" "}
                        · {pad(start.getHours())}:{pad(start.getMinutes())} – {pad(end.getHours())}:
                        {pad(end.getMinutes())}
                      </span>
                    </button>
                    <span className={style.entryDur}>{fmtDuration(e.duracion)}</span>
                    <div className={style.menuWrap}>
                      <button
                        type="button"
                        className={style.menuBtn}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setMenuFor(menuFor === e._id ? null : e._id);
                        }}
                        aria-label="Opciones"
                      >
                        ⋯
                      </button>
                      {menuFor === e._id ? (
                        <div className={style.menu}>
                          <button type="button" onClick={() => handleDeleteEntry(e._id)}>
                            <FiTrash2 /> Eliminar
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {isOpen ? (
                    <div className={style.entryDetail}>
                      {pausas.length ? (
                        <div className={style.pausasBlock}>
                          <span className={style.detailLabel}>Pausas</span>
                          {pausas.map((p, i) => {
                            const pi = new Date(p.inicio);
                            const pf = new Date(p.fin);
                            const dur = Math.max(0, Math.round((pf - pi) / 1000));
                            return (
                              <div key={i} className={style.pausaRow}>
                                <FiPause />
                                {pad(pi.getHours())}:{pad(pi.getMinutes())} → {pad(pf.getHours())}:
                                {pad(pf.getMinutes())} · {fmtDuration(dur)}
                                {p.motivo ? (
                                  <span className={style.pausaMotivo}> · {p.motivo}</span>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span className={style.detailMuted}>Sin pausas.</span>
                      )}

                      <button
                        type="button"
                        className={style.collapseHead}
                        onClick={() => setEntryNotesOpen((v) => !v)}
                      >
                        <span className={style.detailLabel}>Notas</span>
                        <FiChevronDown
                          className={`${style.collapseChevron} ${entryNotesOpen ? style.collapseChevronOpen : ""}`}
                        />
                      </button>
                      {entryNotesOpen ? (
                        <>
                          <textarea
                            className={style.notesArea}
                            value={entryNotesDraft}
                            onChange={(ev) => setEntryNotesDraft(ev.target.value)}
                            placeholder="Notas de esta sesión…"
                            rows={3}
                          />
                          <button
                            type="button"
                            className={style.saveNotesBtn}
                            onClick={() => handleSaveEntryNotes(e._id)}
                          >
                            Guardar notas
                          </button>
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>
      </div>

      {/* Modal Finalizar con notas */}
      {finishOpen ? (
        <div className={style.overlay} onClick={handleCancelFinish} role="presentation">
          <div className={style.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Finalizar sesión</h3>

            <div className={style.field}>
              <span>Resumen</span>
              <div className={style.finishSummary}>
                <div>
                  Tiempo trabajado: <strong>{fmtDuration(activeSecs(running, Date.now()))}</strong>
                </div>
                {(() => {
                  const pausas = [...((running && running.pausas) || [])];
                  if (!pausas.length) return <div className={style.detailMuted}>Sin pausas.</div>;
                  return pausas.map((p, i) => {
                    const pi = new Date(p.inicio);
                    const pf = new Date(p.fin);
                    const dur = Math.max(0, Math.round((pf - pi) / 1000));
                    return (
                      <div key={i} className={style.pausaRow}>
                        <FiPause />
                        {pad(pi.getHours())}:{pad(pi.getMinutes())} → {pad(pf.getHours())}:
                        {pad(pf.getMinutes())} · {fmtDuration(dur)}
                        {p.motivo ? <span className={style.pausaMotivo}> · {p.motivo}</span> : null}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            <div className={style.field}>
              <span>Notas de la sesión (opcional)</span>
              <textarea
                className={style.notesArea}
                value={finishNotes}
                onChange={(e) => setFinishNotes(e.target.value)}
                placeholder="¿Qué hiciste en esta sesión?"
                rows={4}
                autoFocus
              />
            </div>
            <div className={style.modalActions}>
              <button type="button" className={style.ghostBtn} onClick={handleCancelFinish}>
                Cancelar
              </button>
              <button type="button" className={style.saveBtn} onClick={confirmFinish} disabled={saving}>
                {saving ? "Guardando…" : "Guardar sesión"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
